import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OperatorLayout from "./OperatorLayout";
import { Button } from "@/components/ui/button";
import { useOperatorApi } from "@/hooks/useOperatorApi";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

type BillingTab = "overdue" | "mandate" | "email_failures" | "month_close" | "invoices";

export default function OperatorBilling() {
  const { t } = useTranslation();
  const { operatorFetch } = useOperatorApi();
  const [tab, setTab] = useState<BillingTab>("overdue");
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);

  useEffect(() => { loadTab(); }, [tab]);

  async function loadTab() {
    setLoading(true);
    try {
      if (tab === "overdue") {
        const { data } = await supabase.from("provider_profiles")
          .select("*, provider_billing_accounts(*)")
          .eq("billing_status", "past_due")
          .order("created_at", { ascending: false });
        setData(data || []);
      } else if (tab === "mandate") {
        const { data } = await supabase.from("provider_mollie_payments")
          .select("*, provider_profiles(company_name)")
          .order("created_at", { ascending: false });
        setData(data || []);
      } else if (tab === "email_failures") {
        const { data } = await supabase.from("email_dispatches")
          .select("*, provider_profiles(company_name), opportunities(opportunity_number)")
          .in("status", ["failed", "FAILED"])
          .order("created_at", { ascending: false }).limit(50);
        setData(data || []);
      } else if (tab === "month_close") {
        const { data } = await supabase.from("opportunity_billing_lines")
          .select("*, provider_profiles(company_name)")
          .order("created_at", { ascending: false }).limit(100);
        setData(data || []);
      } else if (tab === "invoices") {
        const { data } = await supabase.from("invoices")
          .select("*, provider_profiles(company_name)")
          .order("created_at", { ascending: false }).limit(100);
        setData(data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function closeMonth() {
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    setClosing(true);
    try {
      await operatorFetch("billing/close-month", { method: "POST", body: { month_key: monthKey } });
      toast.success(t("op.billingCloseSuccess", { month: monthKey }));
      loadTab();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setClosing(false);
    }
  }

  const tabs: { id: BillingTab; label: string }[] = [
    { id: "overdue", label: t("op.billingOverdue") },
    { id: "mandate", label: t("op.billingMandate") },
    { id: "email_failures", label: t("op.billingEmailFailures") },
    { id: "month_close", label: t("op.billingMonthClose") },
    { id: "invoices", label: t("op.billingInvoices") },
  ];

  return (
    <OperatorLayout title={t("op.navBilling")} subtitle={t("op.billingSubtitle")}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {tabs.map((tt) => (
              <button key={tt.id} onClick={() => setTab(tt.id)}
                className={cn("px-3 py-1.5 text-xs border transition-colors",
                  tab === tt.id ? "border-accent bg-accent/5 text-foreground" : "border-border text-muted-foreground hover:text-foreground"
                )}>
                {tt.label}
              </button>
            ))}
          </div>
          {tab === "month_close" && (
            <Button variant="commit" size="sm" onClick={closeMonth} disabled={closing}>
              {closing ? t("common.loading") : t("op.billingCloseMonth")}
            </Button>
          )}
        </div>

        <div className="border border-border bg-card overflow-x-auto">
          {loading ? <p className="p-8 text-center text-caption">{t("common.loading")}</p> : data.length === 0 ? (
            <p className="p-8 text-center text-caption">{t("op.billingEmpty")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {tab === "overdue" && <><th className="text-left p-4 text-micro font-normal">{t("op.colCompany")}</th><th className="text-left p-4 text-micro font-normal">{t("op.colBillingStatus")}</th><th className="text-left p-4 text-micro font-normal">{t("op.billingOverdueSince")}</th></>}
                  {tab === "mandate" && <><th className="text-left p-4 text-micro font-normal">{t("op.colCompany")}</th><th className="text-left p-4 text-micro font-normal">{t("op.colStatus")}</th><th className="text-left p-4 text-micro font-normal">{t("op.billingMethods")}</th></>}
                  {tab === "email_failures" && <><th className="text-left p-4 text-micro font-normal">{t("op.colCompany")}</th><th className="text-left p-4 text-micro font-normal">{t("op.oppColNumber")}</th><th className="text-left p-4 text-micro font-normal">{t("op.billingError")}</th><th className="text-left p-4 text-micro font-normal">{t("op.billingAttempts")}</th></>}
                  {tab === "month_close" && <><th className="text-left p-4 text-micro font-normal">{t("op.colProvider")}</th><th className="text-left p-4 text-micro font-normal">{t("op.billingMonth")}</th><th className="text-left p-4 text-micro font-normal">{t("op.billingAmount")}</th><th className="text-left p-4 text-micro font-normal">{t("op.colStatus")}</th></>}
                  {tab === "invoices" && <><th className="text-left p-4 text-micro font-normal">{t("op.colProvider")}</th><th className="text-left p-4 text-micro font-normal">{t("op.billingPeriod")}</th><th className="text-left p-4 text-micro font-normal">{t("op.billingNet")}</th><th className="text-left p-4 text-micro font-normal">{t("op.billingGross")}</th><th className="text-left p-4 text-micro font-normal">{t("op.colStatus")}</th></>}
                </tr>
              </thead>
              <tbody>
                {data.map((item: any) => (
                  <tr key={item.id} className="border-b border-border">
                    {tab === "overdue" && <>
                      <td className="p-4">{item.company_name}</td>
                      <td className="p-4"><span className="text-destructive">{item.billing_status}</span></td>
                      <td className="p-4 text-caption">{item.provider_billing_accounts?.[0]?.past_due_since ? new Date(item.provider_billing_accounts[0].past_due_since).toLocaleDateString("de-DE") : "—"}</td>
                    </>}
                    {tab === "mandate" && <>
                      <td className="p-4">{item.provider_profiles?.company_name || "—"}</td>
                      <td className="p-4"><span className={item.has_payment_method ? "text-accent" : "text-muted-foreground"}>{item.setup_status || "none"}</span></td>
                      <td className="p-4 text-caption">{item.selected_methods?.join(", ") || "—"}</td>
                    </>}
                    {tab === "email_failures" && <>
                      <td className="p-4">{item.provider_profiles?.company_name || "—"}</td>
                      <td className="p-4">{item.opportunities?.opportunity_number || "—"}</td>
                      <td className="p-4 text-caption text-destructive">{item.last_error_code || item.error_message || "—"}</td>
                      <td className="p-4">{item.attempt_count || item.retry_count || 0}</td>
                    </>}
                    {tab === "month_close" && <>
                      <td className="p-4">{item.provider_profiles?.company_name || "—"}</td>
                      <td className="p-4">{item.month_key}</td>
                      <td className="p-4 price-display">{((item.final_cents || 0) / 100).toFixed(2)} €</td>
                      <td className="p-4"><span className="text-xs px-2 py-1 border border-border">{item.status}</span></td>
                    </>}
                    {tab === "invoices" && <>
                      <td className="p-4">{item.provider_profiles?.company_name || "—"}</td>
                      <td className="p-4">{item.period_year}-{String(item.period_month).padStart(2, "0")}</td>
                      <td className="p-4">{Number(item.total_net || 0).toFixed(2)} €</td>
                      <td className="p-4 font-medium">{Number(item.total_gross || 0).toFixed(2)} €</td>
                      <td className="p-4"><span className={cn("text-xs px-2 py-1 border",
                        item.status === "PAID" ? "border-accent/30 text-accent" : "border-border text-muted-foreground"
                      )}>{item.status}</span></td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </OperatorLayout>
  );
}